import React, { useEffect, useMemo, useState } from "react";
import { Download, Link2, Loader2, Presentation, RefreshCw, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_CLS = {
  "fertig": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "in Arbeit": "bg-amber-100 text-amber-800 border-amber-300",
  "nicht begonnen": "bg-slate-100 text-slate-600 border-slate-300",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export const BrandingOverviewTab = () => {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/branding/overview");
      setD(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(
    () => (d?.rows || []).filter((r) => filter === "alle" || r.status === filter),
    [d, filter],
  );

  const openPdf = async (clientId) => {
    try {
      const r = await api.get(`/clients/${clientId}/branding/pdf`, { responseType: "blob", timeout: 240000 });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!d) {
    return <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="branding-overview-loading">
      <Loader2 size={14} className="animate-spin" /> Lädt…
    </div>;
  }

  const s = d.summary || {};
  return (
    <div data-testid="branding-overview">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Selbstvermarktung</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Marken-Guides der Teilnehmer</div>
        </div>
        <button onClick={load} disabled={loading} data-testid="branding-overview-refresh"
          className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={12} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[["Freigeschaltet", s.total, "alle"], ["Guide fertig", s.done, "fertig"],
          ["In Arbeit", s.running, "in Arbeit"], ["Noch nicht begonnen", s.open, "nicht begonnen"]].map(([l, v, f]) => (
            <button key={l} onClick={() => setFilter(f)} data-testid={`branding-overview-filter-${f.replace(/ /g, "-")}`}
              className={`border p-4 text-left transition-colors ${filter === f ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{l}</div>
              <div className="font-heading text-3xl font-light mt-1 text-slate-900">{v ?? 0}</div>
            </button>
        ))}
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left p-3 font-medium">Teilnehmer</th>
              <th className="text-left p-3 font-medium">Coach</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-left p-3 font-medium">Fortschritt</th>
              <th className="text-left p-3 font-medium">Farben</th>
              <th className="text-left p-3 font-medium">Zuletzt</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.client_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`branding-row-${r.client_id}`}>
                <td className="p-3">
                  <a href={`/avgs/${r.client_id}?tab=branding`} className="font-medium text-slate-900 hover:underline">{r.name || "—"}</a>
                  <div className="text-[11px] text-slate-500">{r.email || ""}</div>
                </td>
                <td className="p-3 text-slate-600">{r.coach_name || "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 border ${STATUS_CLS[r.status] || STATUS_CLS["nicht begonnen"]}`}>{r.status}</span>
                  {r.has_social && <span className="ml-1.5 text-[10px] text-slate-500">· Social-Texte</span>}
                  {!r.access && <span className="ml-1.5 text-[10px] text-red-700">· Zugang entzogen</span>}
                </td>
                <td className="p-3 w-40">
                  <div className="h-[3px] bg-slate-200">
                    <div className="h-full bg-slate-900" style={{ width: `${(r.answered / (r.steps || 1)) * 100}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{r.answered}/{r.steps} · {r.asset_count} Bilder</div>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    {(r.colors || []).slice(0, 5).map((c) => (
                      <span key={c} className="h-4 w-4 border border-slate-200" style={{ background: c }} title={c} />
                    ))}
                  </div>
                </td>
                <td className="p-3 font-mono text-slate-500">{fmtDate(r.generated_at || r.updated_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1.5 justify-end">
                    {r.share_link && (
                      <a href={r.share_link} target="_blank" rel="noreferrer" title="Online-Guide"
                        data-testid={`branding-row-share-${r.client_id}`}
                        className="p-1.5 border border-slate-300 hover:bg-slate-100"><Link2 size={12} strokeWidth={1.5} /></a>
                    )}
                    <a href={`/mein-branding?client_id=${r.client_id}`} title="Präsentation öffnen"
                      data-testid={`branding-row-open-${r.client_id}`}
                      className="p-1.5 border border-slate-300 hover:bg-slate-100"><Presentation size={12} strokeWidth={1.5} /></a>
                    {r.answered > 0 && (
                      <button onClick={() => openPdf(r.client_id)} title="Marken-Guide PDF"
                        data-testid={`branding-row-pdf-${r.client_id}`}
                        className="p-1.5 bg-slate-900 text-white hover:bg-black"><Download size={12} strokeWidth={1.5} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500" data-testid="branding-overview-empty">
                <Sparkles size={16} strokeWidth={1.5} className="inline mr-1.5" />
                Hier erscheinen alle Teilnehmer, für die das Branding-Dashboard freigeschaltet ist.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrandingOverviewTab;
